import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { OTPService } from '../../services/opt.service';
import { PersianTitlesService } from '../../../../core/services/persian-titles.service';
import { convertArabicToWestern } from 'src/app/shared/directives/arabic-num-converter.directive';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AuthComponent implements OnInit, OnDestroy {
  mobileForm: FormGroup;
  otpForm: FormGroup;

  otpSubscription: Subscription;

  showMobileForm: boolean = true;
  showOTPForm: boolean = false;

  // Timer
  interval: any;
  leftSeconds: number = 5;

  // Labels
  systemNameLabel: string = '';
  mobileLabel: string = '';
  changeMobileLabel: string = '';
  OTPBoxLabel: string = '';
  getOTPLabel: string = '';
  getOTPAgainLabel: string = '';
  OTPLabel: string = '';
  loginLabel: string = '';
  leftTimeLabel: string = null;
  vpnWarningLabel: string = '';

  constructor(
    private authService: AuthService,
    private otpService: OTPService,
    private persianTitlesService: PersianTitlesService,
    private notifierService: NotifierService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    // this.geoLocationService.getUserLocation().subscribe(
    //   (data) => {
    //     if (data.country !== 'IR') {
    //       this.vpnWarningLabel = 'لطفا جهت دریافت پیامک، فیلترشکن خود را خاموش کنید.';
    //     }
    //   })
    this.initForms();
    if (this.authService.user.value) {
      this.router.navigate(['']);
    }

    // Init Labels
    this.systemNameLabel = this.persianTitlesService.Items.SystemName;
    this.mobileLabel = this.persianTitlesService.Items.Mobile;
    this.changeMobileLabel = this.persianTitlesService.Items.ChangeMobile;
    this.getOTPLabel = this.persianTitlesService.Items.GetOTP;
    this.getOTPAgainLabel = this.persianTitlesService.Items.GetOTPAgain;
    this.OTPLabel = this.persianTitlesService.Items.OTP;
    this.loginLabel = this.persianTitlesService.Items.Login;
  }

  onMobileNumberTextBoxInitialized(e) {
    setTimeout(function () {
      e.component.focus();
    }, 0);
  }

  onGetOTPButton() {
    const convertedMobileNumber = convertArabicToWestern(
      this.mobileForm.value.mobileNumber,
    );
    this.mobileForm.patchValue({ mobileNumber: convertedMobileNumber });

    this.otpSubscription = this.otpService
      .getOTP(convertedMobileNumber)
      .subscribe(
        (resp) => {
          this.showOTPForm = true;
          this.showMobileForm = false;
          if (
            this.mobileForm.valid &&
            this.mobileForm.controls['mobileNumber'].value
          ) {
            this.OTPBoxLabel =
              'کد یکبارمصرفی را که به شماره ' +
              this.mobileForm.controls['mobileNumber'].value +
              ' ارسال شده است، وارد کنید:';
          }
          this.otpForm.patchValue({
            mobileNumber: convertedMobileNumber,
            otpCode: null,
          });
          this.startTimer();
        },
        (error) => {
          this.notifierService.notify('error', error.error.detail);
        },
      );
  }

  onChangeMobileNumberButton() {
    this.showOTPForm = false;
    this.showMobileForm = true;
    clearInterval(this.interval);
  }

  onGetOTPAgainButton() {
    clearInterval(this.interval);
    this.onGetOTPButton();
  }

  onLogin() {
    const convertedMobileNumber = convertArabicToWestern(
      this.otpForm.value.mobileNumber,
    );
    const convertedOtpCode = convertArabicToWestern(this.otpForm.value.otpCode);
    this.otpForm.patchValue({
      mobileNumber: convertedMobileNumber,
      otpCode: convertedOtpCode,
    });

    this.authService.login(convertedMobileNumber, +convertedOtpCode).subscribe({
      next: (response) => {
        let redirectURL: string;
        let params = this.activatedRoute.snapshot.queryParams;
        if (params['redirectURL']) {
          redirectURL = params['redirectURL'];
        }
        if (redirectURL) {
          this.router
            .navigateByUrl(redirectURL)
            .catch(() => this.router.navigate(['']));
        } else {
          this.router.navigate(['']);
        }
      },
      error: (error) => {
        if (!error.ok) {
          switch (error.status) {
            case 0: {
              this.notifierService.notify(
                'error',
                this.persianTitlesService.Phrases.ConnectionError,
              );
              break;
            }
            default: {
              this.notifierService.notify('error', error.error.detail);
              break;
            }
          }
        } else {
          this.notifierService.notify(
            'error',
            this.persianTitlesService.Phrases.UnknownError,
          );
        }
      },
    });
  }

  onMobileNumberInputKeyUp(e: any) {
    if (e.keyCode == 13 && this.mobileForm.valid) {
      this.onGetOTPButton();
    }
  }

  onOTPInputKeyUp(e: any) {
    if (e.keyCode == 13 && this.otpForm.valid) {
      this.onLogin();
    }
  }

  private startTimer() {
    this.leftSeconds = 120;
    this.updateLeftSecondsLabel();
    this.interval = setInterval(() => {
      if (this.leftSeconds > 0) {
        this.leftSeconds--;
        this.updateLeftSecondsLabel();
      } else {
        this.leftSeconds = null;
        this.leftTimeLabel = null;
      }
    }, 1000);
  }

  ngOnDestroy() {
    this.otpSubscription?.unsubscribe();
  }

  private updateLeftSecondsLabel() {
    this.leftTimeLabel =
      this.persianTitlesService.Items.LeftTime +
      ': ' +
      this.leftSeconds +
      ' ' +
      this.persianTitlesService.Items.Second;
  }

  private initForms() {
    this.mobileForm = new FormGroup({
      mobileNumber: new FormControl(null, [
        Validators.required,
        Validators.minLength(11),
        Validators.maxLength(11),
      ]),
    });

    this.otpForm = new FormGroup({
      mobileNumber: new FormControl(null, Validators.required),
      otpCode: new FormControl(null, [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(4),
      ]),
    });
  }
}
